import * as React from "react";

import { useEffect } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import FileHandleViewer from "@/components/FileHandleViewer";
import { PencilLine, ArrowLeft } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Types and Services
import { getIndicators } from "@/services/finance";
import type { IndicatorDetails } from "@/types/finance";
import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";
import { useToast } from "@/components/ui/use-toast";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";
import { RequireEditDocs } from "@/services/require-edit-docs";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/indicators/"
)({
  component: Indicators,
});

function Indicators() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const id = localStorage.getItem("companyId"); // Get companyId from local storage
  const companyId = id?.replace(/^"(.+)"$/, "$1");
  const yearly = Number(localStorage.getItem("year")); // Get year from local storage
  const { company_financial_report } = useAccessPermission();
  const [unit, setUnit] = React.useState(1);
  const [open, setOpen] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["indicators", companyId, yearly],
    queryFn: () =>
      getIndicators({ companyId: companyId as string, yearly: yearly }),
    enabled: Boolean(companyId && yearly),
  });

  const indicatorData = data?.data as IndicatorDetails;

  const dayFromNow = calculateDaysFromNow(indicatorData?.updatedAt);

  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["indicators", companyId, yearly],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  const indicatorDetail = indicatorData?.indicatorDetails;

  const normalIndicator = indicatorDetail?.filter(
    (item) => item.indicatorType === "INDICATOR"
  );
  const publicIndicator = indicatorDetail?.filter(
    (item) => item.indicatorType === "PUBLIC_SECTOR"
  );

  // Effects
  useEffect(() => {
    if (!data?.data?.id && !isLoading) {
      navigate({ to: "/report/finance/company/indicators/add" });
    }
  }, [data, navigate, isLoading]);

  // Function

  const formatNumberWithCommas = (value: number) => {
    // Convert value to a string and remove any existing commas
    const stringValue = String(value || "").replace(/,/g, "");

    // Check if the value is negative
    const isNegative = stringValue.startsWith("-");

    // Remove any non-numeric characters (such as parentheses) and handle negative values
    const numericValue = isNegative
      ? stringValue.slice(1)
      : stringValue.replace(/[()]/g, "");

    // Convert the numeric value to a number
    const numericAmount = Number(numericValue);

    // If the value is invalid (NaN), return the original string value
    if (Number.isNaN(numericAmount)) {
      return value;
    }

    // Format the numeric value with commas
    const formattedValue = numericAmount?.toLocaleString();

    // If it's negative, wrap it in parentheses
    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!indicatorData?.id) return;
    requireEditMutation.mutate({
      editId: indicatorData?.id,
      editRequestType: "INDICATOR_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {yearly}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>
          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {dayFromNow <= 3 ? company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/indicators/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            ) :
              company_financial_report.isUpdate && (
                indicatorData?.editRequestStatus === "REQUEST" ? (
                  <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
                ) : (
                  <Button
                    onClick={() => setOpen(true)}
                    variant="secondary"
                    className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
                  >
                    <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                  </Button>
                ))}
          </div>
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={handleRequireEdit}
          />
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-2 gap-x-2">
            <div className="flex flex-col gap-y-1">
              <Label htmlFor="">ຫົວໜ່ວຍເງິນ (ກີບ)</Label>
              <Select
                onValueChange={(value) => setUnit(Number(value))}
                defaultValue={String(unit)}
                value={String(unit)}
              >
                <SelectTrigger className="w-[120px] indent-1	">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Table className="mb-4">
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:w-[180px] [&>*]:whitespace-nowrap  [&>*]:font-bold [&>*]:text-black">
                  <TableHead style={{ width: 220 }}>ເນຶ້ອໃນລາຍງານ</TableHead>
                  <TableHead>ຈຳນວນ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/*  */}

                <TableRow>
                  <TableCell
                    className="font-bold bg-gray-100 border"
                    colSpan={2}
                  >
                    ບັນດາຕົວຊີ້ວັດທີ່ຈະໄດ້ນຳໃຊ້ຂໍ້ມູນ
                  </TableCell>
                </TableRow>

                {normalIndicator?.map((item) => (
                  <TableRow
                    key={item?.id}
                    className="[&>*]:border  [&>*]:whitespace-nowrap  "
                  >
                    <TableCell>{item?.name}</TableCell>
                    <TableCell className="text-right">
                      {formatNumberWithCommas(item?.amount / unit)}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell
                    className="font-bold bg-gray-100 border"
                    colSpan={2}
                  >
                    ສະເພາະຂະແໜງສາທາລະນະປະໂຫຍດ
                  </TableCell>
                </TableRow>

                {publicIndicator?.map((item) => (
                  <TableRow
                    key={item?.id}
                    className="[&>*]:border  [&>*]:whitespace-nowrap  "
                  >
                    <TableCell>{item?.name}</TableCell>
                    <TableCell className="text-right">
                      {formatNumberWithCommas(item?.amount / unit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {indicatorData?.docFile
              ? company_financial_report.isDownload && (
                <FileHandleViewer fileUrl={indicatorData?.docFile} />
              )
              : null}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
